import type { ContentView } from '@/src/dal/contentful-app-dal'
import { getPrivacyPolicyUrl, getTermsUrl } from '@/src/utils/legalUrls'
import { toAbsoluteUrl } from '@/src/utils/url'

export interface PurchaseScreenCopy {
  paymentHeadline: string
  paymentDescription: string
  paymentChoosePlanText: string
  paymentLegalPrefixText: string
  paymentFirstLegalText: string
  paymentFirstLegalUrl: string
  paymentLegalAndText: string
  paymentSecondLegalText: string
  paymentSecondLegalUrl: string
  paymentPrimaryButtonText: string
  yearlySavingsText: string | null
}

interface PaymentAdditionalTextSections {
  choosePaymentPlan: string
  readPrivacyPolicyIntro: string
  readPrivacyPolicyPPText: string
  readPrivacyPolicyPPLink?: string
  readPrivacyPolicyAnd: string
  readPrivacyPolicyEulaText: string
  readPrivacyPoliyEulaURI?: string
  readPrivacyPolicyEulaURI?: string
}

function getRequiredString(
  value: unknown,
  fieldName: keyof PaymentAdditionalTextSections,
): string {
  if (typeof value === 'string') {
    return value
  }

  throw new Error(`Missing purchase screen copy field: ${fieldName}`)
}

function getOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined
}

function parsePaymentAdditionalTextSections(
  value: unknown,
): PaymentAdditionalTextSections {
  const sections =
    typeof value === 'object' && value != null
      ? (value as Record<string, unknown>)
      : {}

  return {
    choosePaymentPlan: getRequiredString(
      sections.choosePaymentPlan,
      'choosePaymentPlan',
    ),
    readPrivacyPolicyIntro: getRequiredString(
      sections.readPrivacyPolicyIntro,
      'readPrivacyPolicyIntro',
    ),
    readPrivacyPolicyPPText: getRequiredString(
      sections.readPrivacyPolicyPPText,
      'readPrivacyPolicyPPText',
    ),
    readPrivacyPolicyPPLink: getOptionalString(
      sections.readPrivacyPolicyPPLink,
    ),
    readPrivacyPolicyAnd: getRequiredString(
      sections.readPrivacyPolicyAnd,
      'readPrivacyPolicyAnd',
    ),
    readPrivacyPolicyEulaText: getRequiredString(
      sections.readPrivacyPolicyEulaText,
      'readPrivacyPolicyEulaText',
    ),
    readPrivacyPoliyEulaURI: getOptionalString(
      sections.readPrivacyPoliyEulaURI,
    ),
    readPrivacyPolicyEulaURI: getOptionalString(
      sections.readPrivacyPolicyEulaURI,
    ),
  }
}

export function getPaymentScreenCopy({
  paymentView,
  t,
  yearlySavingsFormatted,
}: {
  paymentView: ContentView | null | undefined
  t: (key: string, options?: Record<string, unknown>) => string
  yearlySavingsFormatted: string | null
}): PurchaseScreenCopy {
  const yearlySavingsText =
    yearlySavingsFormatted == null
      ? null
      : t('purchase:yearly_savings', { amount: yearlySavingsFormatted })
  const paymentAdditionalTextSections = parsePaymentAdditionalTextSections(
    paymentView?.additionalTextSections,
  )

  return {
    paymentHeadline: paymentView?.headline ?? '',
    paymentDescription: paymentView?.textContent ?? '',
    paymentChoosePlanText: paymentAdditionalTextSections.choosePaymentPlan,
    paymentLegalPrefixText:
      paymentAdditionalTextSections.readPrivacyPolicyIntro,
    paymentFirstLegalText:
      paymentAdditionalTextSections.readPrivacyPolicyPPText,
    paymentFirstLegalUrl: toAbsoluteUrl(
      paymentAdditionalTextSections.readPrivacyPolicyPPLink ??
        getPrivacyPolicyUrl(),
    ),
    paymentLegalAndText: paymentAdditionalTextSections.readPrivacyPolicyAnd,
    paymentSecondLegalText:
      paymentAdditionalTextSections.readPrivacyPolicyEulaText,
    paymentSecondLegalUrl: toAbsoluteUrl(
      paymentAdditionalTextSections.readPrivacyPoliyEulaURI ??
        paymentAdditionalTextSections.readPrivacyPolicyEulaURI ??
        getTermsUrl(),
    ),
    paymentPrimaryButtonText: paymentView?.primaryButtonText ?? '',
    yearlySavingsText,
  }
}
